import { FEATURES } from '@constants/Text'
import {
  routeModuleKey,
  DYNAMIC_ROUTE,
  VISIBILITY_GATE,
} from '@modules/navegacao-v2/utils/visible-modules'
import { ROUTE_DEFS, ROUTE_KEYS } from '@constants/navigation-routes'

export {
  DYNAMIC_ROUTE,
  VISIBILITY_GATE,
} from '@modules/navegacao-v2/utils/visible-modules'

/**
 * Deep-links do CONTEXTO CLIENTE. Alguns módulos entram numa "Visão Geral",
 * "Bem-vindo" (upsell) ou painel AGREGADO de todos os clientes — telas que não
 * fazem sentido quando estamos na visão de UM cliente. Quando o módulo é acessado
 * pela visão cliente (e o escritório TEM acesso — itens em upsell mantêm a
 * landing), pulamos direto para a 1ª tela escopável pelo cliente do contexto.
 * Resolvido em resolveDynamicRoute(item, { isCliente }) e também usado como
 * destino do guard de rota em src/router/index.js (ver resolveClienteHiddenRedirect).
 *   - Portfólio Review: 'simulador' → histórico de simulações do cliente
 *   - CRM: 'crm' (welcome/overview agregada) → relacionamento (auto-filtra o cliente)
 *   - Compliance: 'compliance' (welcome) → visão geral (auto-filtra o cliente)
 *   - Consolidador: 'carteira' (overview agregada) → carteira do cliente
 *   - AAWZ Drive: 'aawz-drive' (welcome) → arquivos
 *   - FP: 'fp' (welcome) → home do cliente
 */
const CLIENTE_SKIP_TO = {
  PORTFOLIO_REVIEW: 'simulador.historico',
  CRM: 'crm.relationship',
  COMPLIANCE: 'compliance.overview',
  CONSOLIDADOR: 'carteira.minha',
  DRIVE: 'aawz-drive.files',
  FP: 'fp.home',
}

/**
 * Abas do NAV-BAR INTERNO de cada módulo (crm-nav-bar.vue, compliance-nav-bar.vue)
 * que não fazem sentido na visão de UM cliente — "Bem-vindo" é a landing de
 * upsell/agregado e "Visão Geral" do CRM é o painel agregado de TODOS os
 * clientes. Na visão cliente a navegação já entra direto na aba certa
 * (CLIENTE_SKIP_TO / clienteEntranceModule), então essas abas ficam
 * escondidas na NavBarNavigation em vez de removidas do catálogo de rotas de
 * cada módulo. Consumido por filterClienteHiddenRoutes (ver
 * @modules/navegacao-v2/utils/cliente-hidden-routes.js) dentro do computed
 * `routes()` de cada nav-bar.
 */
export const CLIENTE_HIDDEN_ROUTES = {
  CRM: ['crm.welcome', 'crm.overview'],
  COMPLIANCE: ['compliance.welcome'],
  CONSOLIDADOR: ['carteira.overview'],
  DRIVE: ['aawz-drive.welcome'],
  FP: ['fp.welcome'],
}

/**
 * true se `routeName` é uma das abas escondidas em contexto cliente (ver
 * CLIENTE_HIDDEN_ROUTES acima). Fonte única usada tanto pelo guard de rota
 * (router/index.js — bloqueia acesso direto por URL) quanto pela resync de
 * contexto no reload (navegacao-v2-sidebar.vue syncContextFromRoute) — evita
 * que uma rota agregada/gestão "herde" contexto cliente só por compartilhar
 * o mesmo módulo de uma rota cliente legítima.
 * @param {string} routeName
 * @returns {boolean}
 */
export function isClienteHiddenRoute(routeName) {
  return Object.values(CLIENTE_HIDDEN_ROUTES).some((names) =>
    names.includes(routeName)
  )
}

/**
 * Destino de redirect quando `routeName` é uma aba escondida em contexto
 * cliente. Usado pelo guard de rota para não deixar o usuário numa página
 * sem aba ativa (ver router/index.js). Retorna null se a rota não está
 * escondida ou não há skip mapeado para o módulo dela.
 * @param {string} routeName
 * @returns {string|null}
 */
export function resolveClienteHiddenRedirect(routeName) {
  const moduleKey = Object.keys(CLIENTE_HIDDEN_ROUTES).find((key) =>
    CLIENTE_HIDDEN_ROUTES[key].includes(routeName)
  )
  return (moduleKey && CLIENTE_SKIP_TO[moduleKey]) || null
}

/**
 * Mapa de módulos por contexto de navegação (gestão / cliente).
 *
 * O ESQUELETO de cada item (label, icon, routeName, flag) vem de `ROUTE_DEFS`
 * (fonte ÚNICA, extraída VERBATIM da nav antiga em src/components/nav-bar.vue).
 * Aqui apenas referenciamos a key e, quando preciso, FORMATAMOS:
 *   - routeName do Consolidador é por contexto (gestão=overview, cliente=minha)
 *   - Comissões/Tesouraria têm destino dinâmico (dynamicRoute → resolvido no select)
 *   - CRM/Compliance/Drive têm comportamento de upsell (upsellFlag → item acinzentado)
 *   - Tesouraria gateia por subrotas (visibilityGate → treasuryVisible)
 *
 * Cada item resultante: { key, label, routeName, icon, flag?, ...markers }
 * Visibilidade final é filtrada por visibleGroups(groups, $access, isInternal).
 */

/**
 * Monta um item da nova nav a partir de uma key do ROUTE_DEFS.
 * label ← title, icon ← icon, routeName ← routeName, flag ← flag (todos VERBATIM).
 * `overrides` permite key própria da nova nav, flag explícita e marcadores.
 * @param {string} routeKey  key do ROUTE_DEFS (ROUTE_KEYS.*)
 * @param {Object} overrides { key, flag, ...markers }
 * @returns {Object}
 */
function fromDef(routeKey, overrides = {}) {
  const d = ROUTE_DEFS[routeKey]
  const base = {
    key: overrides.key || routeKey,
    label: d.title.trim(),
    routeName: d.routeName,
    icon: d.icon,
  }
  // flag: usa a do ROUTE_DEFS por padrão; override explícito quando a nav antiga
  // gateia por algo que não é flag simples (ex: Consolidador por ASSET_LOCATION).
  const flag = overrides.hasOwnProperty('flag') ? overrides.flag : d.flag
  if (flag) base.flag = flag
  const rest = { ...overrides }
  delete rest.key
  delete rest.flag
  return { ...base, ...rest }
}

export const GESTAO_GROUPS = [
  {
    title: 'Análise',
    items: [
      // Consolidador (gestão): rota POR CONTEXTO (overview), flag ASSET_LOCATION (nav antiga).
      fromDef(ROUTE_KEYS.CONSOLIDADOR, {
        key: 'consolidador-geral',
        flag: FEATURES.ASSET_LOCATION,
        dynamicRoute: DYNAMIC_ROUTE.CONSOLIDADOR,
      }),
      fromDef(ROUTE_KEYS.DASHBOARD_BI, { key: 'bi' }),
      fromDef(ROUTE_KEYS.ANALISE_MERCADO, { key: 'analise_mercado' }),
      fromDef(ROUTE_KEYS.OPPORTUNITIES, { key: 'oportunidades' }),
    ],
  },
  {
    title: 'Gestão do escritório',
    items: [
      fromDef(ROUTE_KEYS.FINANCE, { key: 'financeiro' }),
      // Comissões: destino real via handler (visao-geral/fechamentos), flag COMISSOES.
      fromDef(ROUTE_KEYS.COMISSOES, {
        key: 'comissoes',
        flag: FEATURES.COMISSOES,
        dynamicRoute: DYNAMIC_ROUTE.COMISSOES,
      }),
      // Tesouraria: visibilidade por subrotas + destino real (pagar/receber/solicitações).
      fromDef(ROUTE_KEYS.TREASURY, {
        key: 'tesouraria',
        flag: null,
        visibilityGate: VISIBILITY_GATE.TREASURY_SUBROUTES,
        dynamicRoute: DYNAMIC_ROUTE.TREASURY,
      }),
      fromDef(ROUTE_KEYS.VALUATION, { key: 'estrategia' }),
      fromDef(ROUTE_KEYS.BILLING_SYSTEM, { key: 'cobrancas' }),
      fromDef(ROUTE_KEYS.CAPTABLE, { key: 'partnership' }),
    ],
  },
  {
    title: 'Relacionamento',
    items: [
      fromDef(ROUTE_KEYS.CLIENTES, { key: 'clientes', isGateway: true }),
      // CRM: upsell — aparece acinzentado para quem não tem acesso (cross-sell visível).
      fromDef(ROUTE_KEYS.CRM, {
        key: 'crm',
        flag: null,
        upsellFlag: FEATURES.CRM,
      }),
      fromDef(ROUTE_KEYS.WS, { key: 'ws' }),
      fromDef(ROUTE_KEYS.PROFESSIONALS, { key: 'profissionais' }),
      // RH: "Gestão de Desempenho" (valor da nav antiga; fromDef faz trim do espaço final).
      fromDef(ROUTE_KEYS.RH, { key: 'rh' }),
    ],
  },
  {
    title: 'Ferramentas',
    items: [
      fromDef(ROUTE_KEYS.DATA_ROOM, { key: 'data_room' }),
      // AAWZ Drive: também é upsell na nav antiga (flag OU !hideCrossSell).
      fromDef(ROUTE_KEYS.AAWZ_DRIVE, {
        key: 'drive',
        flag: null,
        upsellFlag: FEATURES.AAWZ_DRIVE,
      }),
      // AAWZ Intelligence: upsell — aparece acinzentado pra quem não tem
      // CHAT_IA (cross-sell visível), mesmo padrão de CRM/AAWZ Drive.
      fromDef(ROUTE_KEYS.AAWZ_INTELLIGENCE, {
        key: 'intelligence',
        flag: null,
        upsellFlag: FEATURES.CHAT_IA,
      }),
      fromDef(ROUTE_KEYS.TAREFAS, { key: 'tarefas' }),
      fromDef(ROUTE_KEYS.WORKFLOW_ENGINE, { key: 'workflow-engine' }),
    ],
  },
]

export const CLIENTE_GROUPS = [
  {
    title: 'Cliente',
    items: [
      // "Perfil" é tela própria da navegação 2026 (não existe na nav antiga);
      // mantém routeName/icon próprios, gateado pela flag NAVEGACAO_2026.
      {
        key: 'perfil',
        label: 'Perfil',
        routeName: 'cliente-perfil',
        icon: 'profile-tick',
        flag: FEATURES.NAVEGACAO_2026,
      },
    ],
  },
  {
    title: 'Carteira',
    items: [
      // Consolidador (cliente): rota POR CONTEXTO → carteira.minha (cliente selecionado).
      fromDef(ROUTE_KEYS.CONSOLIDADOR_MINHA, {
        key: 'consolidador',
        flag: FEATURES.ASSET_LOCATION,
        dynamicRoute: DYNAMIC_ROUTE.CONSOLIDADOR,
        // ★ [spec-009 T018] O DEEP LINK da carteira de UM cliente — a rota-filha
        // que carrega o cliente na URL (`consolidator/routes.js:311`,
        // `c/:id_cliente_route`). Quem navega COM um cliente em mãos (hoje o
        // `abrir_cliente` da IA) usa este destino; a sidebar continua na
        // `carteira.minha` do cliente ativo.
        //
        // ⚠️ **Não é `clienteSkipTo`, e a diferença não é de nome.** O
        // `resolveDynamicRoute` devolve o `clienteSkipTo` como NOME puro, sem
        // params (`utils/resolve-dynamic-route.js:27-28`), e o param desta
        // rota-filha é OBRIGATÓRIO (sem `?`): pela sidebar, o push cairia numa
        // rota que exige um param que ninguém montou.
        clienteDeepLink: {
          routeName: 'carteira.minha.cliente',
          paramKey: 'id_cliente_route',
        },
        // ★ Desde 27/08/2026 a rota abre o cliente da URL sozinha
        // (`minha-carteira.vue`, `hidratarClienteDaRota`, no molde do
        // `client-profile`). Até então ela recebia o param e NÃO o abria: o
        // `openRouteClient` comparava `v.id === id_cliente` — número contra o
        // param, que é string — e o `find` nunca achava ninguém, com a store
        // quente ou fria ([G-1] do QA da spec-009).
        // O mesmo fato vive em `ROTAS_QUE_HIDRATAM_PELO_PARAM`
        // (`abrir-cliente.js`), e o teste da tool confronta os dois: declaração
        // que contradiz a flag fica vermelha.
        entraNoContextoSozinha: true,
      }),
      fromDef(ROUTE_KEYS.SIMULADOR, {
        key: 'portfolio_review',
        clienteSkipTo: CLIENTE_SKIP_TO.PORTFOLIO_REVIEW,
      }),
      // Wealth Service (cliente): pula o dashboard agregado (ws.clients, TODOS
      // os clientes) e entra direto na carteira do cliente ativo
      // (ws.clients.codigo) — o resolver do módulo (clienteEntranceModule: 'ws',
      // registrado em @modules/ws/index.js) resolve o routeName + params (id do
      // cliente) sem chamada de backend. Sem modal — só navegação.
      fromDef(ROUTE_KEYS.WS, {
        key: 'cli-ws',
        clienteModal: true,
        clienteEntranceModule: 'ws',
      }),
      // Cobranças: sem aba "Bem-vindo" e sem rota agregada distinta da rota
      // por cliente — pousa direto em billing-system.overview, que já tem
      // filtro de cliente pré-selecionado (ver view-billing-system-overview.vue).
      // Flag herdada do ROUTE_DEFS (FEATURES.COBRANCAS_CVM), sem override.
      fromDef(ROUTE_KEYS.BILLING_SYSTEM, { key: 'cli-cobrancas' }),
    ],
  },
  {
    title: 'Planejamento',
    items: [
      // FP: flag FINANCIAL_PLANNING_SIMULATOR (a flag de acesso real, não a legacy PGBL).
      fromDef(ROUTE_KEYS.FP_HOME, {
        key: 'fp',
        flag: FEATURES.FINANCIAL_PLANNING_SIMULATOR,
      }),
      // Compliance: upsell na nav antiga (canShowOrUpsell). Na visão de cliente,
      // abre o modal "Visão geral cliente" focado no cliente ativo (clienteModal)
      // — o resolver do módulo (clienteEntranceModule: 'compliance', registrado
      // em @modules/compliance/index.js) decide rota (sempre compliance.overview,
      // sem ambiguidade de mode) + payload do modal. Sem cliente ativo, em
      // upsell, ou sem resolver registrado, cai no clienteSkipTo
      // (compliance.overview, que auto-filtra o cliente) — ver onSelect na sidebar.
      fromDef(ROUTE_KEYS.COMPLIANCE, {
        key: 'compliance',
        flag: null,
        upsellFlag: FEATURES.COMPLIANCE,
        clienteSkipTo: CLIENTE_SKIP_TO.COMPLIANCE,
        clienteModal: true,
        clienteEntranceModule: 'compliance',
      }),
    ],
  },
  {
    title: 'Relacionamento',
    items: [
      // CRM na visão de cliente: abre o modal cliente-360 focado no cliente ativo
      // (clienteModal) — o resolver do módulo (clienteEntranceModule: 'crm',
      // registrado em @modules/crm/index.js) decide, via backend, se navega para
      // crm.relationship ou crm.leads e com qual mode o modal abre. Sem cliente
      // ativo, em upsell, ou sem resolver registrado, cai no clienteSkipTo
      // (crm.relationship, que auto-filtra o cliente) — ver onSelect na sidebar.
      fromDef(ROUTE_KEYS.CRM, {
        key: 'cli-crm',
        flag: null,
        upsellFlag: FEATURES.CRM,
        clienteSkipTo: CLIENTE_SKIP_TO.CRM,
        clienteModal: true,
        clienteEntranceModule: 'crm',
      }),
      fromDef(ROUTE_KEYS.TAREFAS, { key: 'cli-tarefas' }),
    ],
  },
  {
    title: 'Ferramentas',
    items: [
      fromDef(ROUTE_KEYS.AAWZ_DRIVE, {
        key: 'cli-drive',
        flag: null,
        upsellFlag: FEATURES.AAWZ_DRIVE,
        clienteSkipTo: CLIENTE_SKIP_TO.DRIVE,
      }),
      // Backoffice: visibilidade real não é a flag simples — a rota exige
      // canOperateBackOffice (IS_INTERNAL OU BACK_OFFICE_SELF_SERVICE) E a
      // flag BACK_OFFICE (canShowBackOfficeRoute, reaproveitada de
      // @modules/back-office/utils/permissions). flag: null aqui porque o
      // visibilityGate já decide tudo — sem aba "Bem-vindo" e sem rota
      // agregada distinta; pousa em back-office.imports, com o cliente ativo
      // pré-selecionado no filtro (kanban-imports.vue) e na comparação de
      // contas (table-comparison-investment-accounts.vue).
      fromDef(ROUTE_KEYS.BACK_OFFICE, {
        key: 'cli-back-office',
        flag: null,
        visibilityGate: VISIBILITY_GATE.BACK_OFFICE_ACCESS,
      }),
    ],
  },
]

/**
 * Retorna o label do módulo cuja rota corresponde a `routeName` (busca nos dois
 * contextos). Casa por rota exata e, se não houver, por sub-rota (ex:
 * 'crm.bem-vindo' => 'CRM'). Retorna '' se não encontrar.
 */
export function moduleLabelByRoute(routeName) {
  const match = moduleByRoute(routeName)
  return match ? match.label : ''
}

/**
 * Retorna o item de módulo (com label + icon) cuja rota corresponde a
 * `routeName` (casa por chave de módulo). Retorna null se não encontrar.
 */
export function moduleByRoute(routeName) {
  const key = routeModuleKey(routeName)
  if (!key) return null
  const all = [...GESTAO_GROUPS, ...CLIENTE_GROUPS].flatMap((g) => g.items)
  return all.find((it) => routeModuleKey(it.routeName) === key) || null
}

// Destino ao entrar num cliente (rota do Consolidador — redireciona p/ visão geral).
export const CLIENTE_FIRST_ROUTE = 'cliente-perfil'
// Destino ao voltar para gestão (gateway de clientes = tela real do módulo Clientes).
export const GESTAO_GATEWAY_ROUTE = 'clientes'
